/**
 * AccountsAccordion — Category-grouped bank accounts
 *
 * Three categories: Checking, Savings, Credit Cards.
 * Top-level headers use generic gray icons.
 * Individual account rows use bank-branded BankLogo circles.
 * Tapping an account row navigates to Account Detail page.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Landmark, PiggyBank, CreditCard } from 'lucide-react';
import { BankLogo } from '@/components/BankLogo';
import { T } from '@/styles/tokens';
import { AnimatedAccordion, StaggerRow } from '@/components/ui/AnimatedAccordion';

export interface PlaidAccount {
  id: string;
  name: string;
  mask: string;
  subtype: string;
  balanceCents: number;
  institutionName: string;
  plaidLogo?: string | null;
  plaidColor?: string | null;
  status?: 'connected' | 'needs_reauth' | 'error' | null;
}

interface AccountsAccordionProps {
  accounts: PlaidAccount[];
  onConnectAnother?: () => void;
}

const CATEGORIES = [
  { key: 'checking', label: 'Checking', Icon: Landmark },
  { key: 'credit', label: 'Credit Cards', Icon: CreditCard },
  { key: 'savings', label: 'Savings', Icon: PiggyBank },
] as const;

function formatBalance(cents: number): string {
  const abs = Math.round(Math.abs(cents) / 100);
  const prefix = cents < 0 ? '-' : '';
  return `${prefix}$${abs.toLocaleString()}`;
}

export function AccountsAccordion({ accounts, onConnectAnother }: AccountsAccordionProps) {
  const navigate = useNavigate();

  const categoryData = useMemo(() => {
    return CATEGORIES.map((cat) => {
      const catAccounts = accounts.filter((a) => a.subtype === cat.key);
      const totalCents = catAccounts.reduce((sum, a) => sum + a.balanceCents, 0);
      return { ...cat, catAccounts, totalCents };
    });
  }, [accounts]);

  return (
    <div>
      {categoryData.map((cat, catIdx) => {
        const CatIcon = cat.Icon;
        const totalStr = cat.catAccounts.length > 0 ? formatBalance(cat.totalCents) : '';
        const hasIssue = cat.catAccounts.some((a) => a.status === 'needs_reauth');

        return (
          <div key={cat.key}>
            <AnimatedAccordion
              icon={
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  background: 'rgba(26,26,26,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}>
                  <CatIcon size={18} color={T.textSecondary} />
                  {hasIssue && (
                    <div style={{
                      position: 'absolute', top: -1, right: -1,
                      width: 9, height: 9, borderRadius: '50%',
                      backgroundColor: T.warning,
                      border: `2px solid ${T.white}`,
                    }} />
                  )}
                </div>
              }
              label={cat.label}
              total={totalStr}
              totalColor={cat.key === 'credit' && cat.totalCents < 0 ? T.danger : T.text}
              innerBg="#F8F5EF"
              innerRadius="0"
            >
              {(showRows) => (
                <>
                  {cat.catAccounts.map((acct, i) => (
                    <StaggerRow key={acct.id} showRows={showRows} index={i}>
                      <>
                        {i > 0 && (
                          <div style={{ height: 1, backgroundColor: 'rgba(0,0,0,0.04)', margin: '0 16px' }} />
                        )}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/app/account/${acct.id}`);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '9px 16px 9px 20px',
                            cursor: 'pointer',
                            backgroundColor: T.sand,
                          }}
                        >
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <BankLogo
                              institutionName={acct.institutionName}
                              plaidLogo={acct.plaidLogo}
                              plaidColor={acct.plaidColor}
                              size={30}
                            />
                            {acct.status === 'needs_reauth' && (
                              <div style={{
                                position: 'absolute', top: -2, right: -2,
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: T.warning,
                                border: `1.5px solid ${T.sand}`,
                              }} />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: T.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap' as const,
                              lineHeight: 1.2,
                            }}>
                              {acct.name}
                            </div>
                            <div style={{ fontSize: 11, color: T.gray400, marginTop: 2, lineHeight: 1.2 }}>
                              ····{acct.mask} · {acct.institutionName}
                            </div>
                          </div>
                          <span style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: acct.balanceCents >= 0 ? T.text : T.textSecondary,
                            fontVariantNumeric: 'tabular-nums',
                            flexShrink: 0,
                          }}>
                            {formatBalance(acct.balanceCents)}
                          </span>
                        </div>
                      </>
                    </StaggerRow>
                  ))}

                  {/* Connect another */}
                  <StaggerRow showRows={showRows} index={cat.catAccounts.length}>
                    <div
                      onClick={(e) => { e.stopPropagation(); onConnectAnother?.(); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '6px 16px 4px 20px',
                        cursor: 'pointer',
                        backgroundColor: T.sand,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.primary }}>
                        Connect another
                      </span>
                      <svg width="10" height="10" viewBox="0 0 10 10">
                        <path d="M3 2L7 5L3 8" stroke="currentColor" strokeWidth="1.5"
                          strokeLinecap="round" strokeLinejoin="round" fill="none"
                          style={{ color: T.primary }} />
                      </svg>
                    </div>
                  </StaggerRow>
                </>
              )}
            </AnimatedAccordion>

            {/* Divider between categories */}
            {catIdx < categoryData.length - 1 && (
              <div style={{ height: 1, background: 'rgba(0,0,0,0.05)', margin: '0 16px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
